import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Save, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { useBankReconciliation } from '@/hooks/useBankReconciliation';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { PaymentMethodTotals, ReconciliationEntry } from '@/types/finance';
import { cn } from '@/lib/utils';

interface BankReconciliationPanelProps {
  startDate: Date;
  endDate: Date;
  timezone: string;
  systemTotals: PaymentMethodTotals | null;
  isLoadingSystem: boolean;
}

const getVarianceClass = (variance: number, systemAmount: number) => {
  if (variance === 0) return 'text-success';
  const percentDiff = systemAmount > 0 ? Math.abs(variance / systemAmount) * 100 : 0;
  if (percentDiff <= 5) return 'text-warning';
  return 'text-destructive';
};

const StatusBadge = ({ status }: { status: string }) => {
  switch (status) {
    case 'reconciled':
      return (
        <Badge variant="success">
          <CheckCircle className="w-3 h-3 mr-1" />
          Reconciled
        </Badge>
      );
    case 'in_progress':
      return (
        <Badge variant="secondary">
          <Clock className="w-3 h-3 mr-1" />
          In Progress
        </Badge>
      );
    default:
      return (
        <Badge variant="outline">
          <AlertCircle className="w-3 h-3 mr-1" />
          Not Started
        </Badge>
      );
  }
};

export const BankReconciliationPanel = ({
  startDate,
  endDate,
  timezone,
  systemTotals,
  isLoadingSystem,
}: BankReconciliationPanelProps) => {
  const { formatDate, timezoneShortCode } = useTimezoneUtils();
  const { formatMoney: formatCurrency } = useCompanyLocale();
  const {
    entries,
    totals,
    overallStatus,
    isLoading,
    isSaving,
    save,
  } = useBankReconciliation({ startDate, endDate, timezone, systemTotals });

  // Local state for form inputs
  const [localEntries, setLocalEntries] = useState<ReconciliationEntry[]>([]);

  // Sync local state with fetched data
  useEffect(() => {
    if (entries.length > 0) {
      setLocalEntries(entries);
    }
  }, [entries]);

  const handleActualChange = (index: number, value: string) => {
    const numValue = value === '' ? null : parseFloat(value);
    setLocalEntries(prev => prev.map((entry, i) => 
      i === index 
        ? { 
            ...entry, 
            actual_received: numValue,
            variance: numValue !== null ? numValue - entry.system_amount : 0,
          } 
        : entry
    ));
  };

  const handleNotesChange = (index: number, value: string) => {
    setLocalEntries(prev => prev.map((entry, i) => 
      i === index ? { ...entry, notes: value } : entry
    ));
  };

  const handleSave = (markAsReconciled: boolean = false) => {
    const entriesToSave = localEntries.map(entry => ({
      ...entry,
      status: markAsReconciled ? 'reconciled' as const : 'draft' as const,
    }));
    save(entriesToSave);
  };

  const localTotals = {
    system: localEntries.reduce((sum, e) => sum + e.system_amount, 0),
    actual: localEntries.reduce((sum, e) => sum + (e.actual_received || 0), 0),
    variance: localEntries.reduce((sum, e) => sum + e.variance, 0),
  };

  if (isLoading || isLoadingSystem) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-3">
            Bank Reconciliation
            <StatusBadge status={overallStatus} />
          </CardTitle>
          <CardDescription>
            <span className="font-medium text-foreground">
              Period: {formatDate(startDate, 'd MMM yyyy')} – {formatDate(endDate, 'd MMM yyyy')} ({timezoneShortCode})
            </span>
            <br />
            Compare system payment totals against actual bank deposits
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSave(false)}
            disabled={isSaving}
          >
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </Button>
          <Button
            size="sm"
            onClick={() => handleSave(true)}
            disabled={isSaving}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Mark Reconciled
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Payment Method</TableHead>
              <TableHead className="text-right w-[140px]">System Shows</TableHead>
              <TableHead className="text-right w-[160px]">Actual Received</TableHead>
              <TableHead className="text-right w-[140px]">Variance</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {localEntries.map((entry, index) => (
              <TableRow key={entry.payment_method}>
                <TableCell className="font-medium">{entry.label}</TableCell>
                <TableCell className="text-right font-mono">
                  {formatCurrency(entry.system_amount)}
                </TableCell>
                <TableCell className="text-right">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter amount"
                    value={entry.actual_received ?? ''}
                    onChange={(e) => handleActualChange(index, e.target.value)}
                    className="w-full text-right font-mono"
                  />
                </TableCell>
                <TableCell className={cn(
                  'text-right font-mono font-medium',
                  entry.actual_received !== null && getVarianceClass(entry.variance, entry.system_amount)
                )}>
                  {entry.actual_received !== null 
                    ? formatCurrency(entry.variance)
                    : '—'
                  }
                </TableCell>
                <TableCell>
                  <Textarea
                    placeholder="e.g., Card fees, timing difference..."
                    value={entry.notes}
                    onChange={(e) => handleNotesChange(index, e.target.value)}
                    className="min-h-[36px] resize-none"
                    rows={1}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow className="bg-muted/50 font-semibold">
              <TableCell>Total</TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(localTotals.system)}
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(localTotals.actual)}
              </TableCell>
              <TableCell className={cn(
                'text-right font-mono',
                getVarianceClass(localTotals.variance, localTotals.system)
              )}>
                {formatCurrency(localTotals.variance)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableFooter>
        </Table>

        {/* Help text for card payments */}
        <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
          <p className="font-medium mb-1">Understanding Variances</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Card/EFTPOS:</strong> Variance is typically due to processor fees (1.5-3%), chargebacks, or settlement timing</li>
            <li><strong>Cash:</strong> Variance may indicate counting errors or unrecorded deposits</li>
            <li><strong>Bank Transfer:</strong> Check for timing differences between invoice date and deposit date</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
